<template>
  <eg-b-dropdown
    ref="dropdown"
    :class="['essay-actions-dropdown', customButtonClass]"
    :variant="variant"
    no-caret
  >
    <template #button-content>
      <slot name="button-content">
        <eg-icon icon="more-options-vertical-dots-dots" aria-hidden="true" />
      </slot>
    </template>

    <eg-b-dropdown-item v-if="$listeners.print" @click="printEssay">
      <eg-icon icon="print" size="small" />
      <span class=""> {{ $t("essays.printReport") }} </span>
    </eg-b-dropdown-item>

    <eg-b-dropdown-item
      v-if="$listeners.export && !isFailedStatus"
      @click="$emit('export', { type: 'pdf', essay })"
    >
      <eg-icon icon="download" size="small" />
      <span class=""> {{ $t("essays.exportToPdf") }} </span>
    </eg-b-dropdown-item>

    <eg-b-dropdown-item
      v-if="
        $listeners.export &&
        !isFailedStatus &&
        isGoogleFolder &&
        isFolderConnected
      "
      @click="$emit('export', { type: 'google', essay })"
    >
      <eg-icon icon="class-group" size="small" />
      <span class=""> {{ $t("essays.exportToGoogleClassroom") }} </span>
      <div v-if="essay.exportedAt" class="small-text">
        {{ $t("essays.lastExported") }} {{ formatDate(essay.exportedAt) }}
      </div>
    </eg-b-dropdown-item>

    <eg-b-dropdown-item
      v-if="
        $listeners.export &&
        !isFailedStatus &&
        isCanvasFolder &&
        (isFolderConnected || features.bulkImports)
      "
      @click="$emit('export', { type: 'canvas', essay })"
    >
      <eg-icon icon="class-group" size="small" />
      <span class=""> {{ $t("essays.exportToCanvas") }} </span>
      <div v-if="essay.exportedAt" class="small-text">
        {{ $t("essays.lastExported") }} {{ formatDate(essay.exportedAt) }}
      </div>
    </eg-b-dropdown-item>

    <eg-b-dropdown-divider v-if="showExportSection" />

    <eg-b-dropdown-item
      v-if="$listeners.rename"
      @click="$emit('rename', essay)"
    >
      <eg-icon icon="pen-edit" size="small" />
      <span class=""> {{ $t("essays.editEssayName") }} </span>
    </eg-b-dropdown-item>

    <eg-b-dropdown-item
      v-if="$listeners.regrade"
      @click="$emit('regrade', essay)"
    >
      <eg-icon icon="refresh-rotate" size="small" />
      <span class=""> {{ $t("essays.regrade") }} </span>
    </eg-b-dropdown-item>

    <!-- <eg-b-dropdown-item>
      <eg-icon icon="move-to" size="small" />
      <span class=""> Move to </span>
    </eg-b-dropdown-item> -->

    <eg-b-dropdown-item
      v-if="$listeners.copy && isV3"
      @click="$emit('copy', 'feedback')"
    >
      <eg-icon icon="copy" size="small" />
      <span class=""> {{ $t("essays.copyFeedbackReport") }} </span>
    </eg-b-dropdown-item>

    <eg-b-dropdown-item
      v-if="$listeners.copy && isV3"
      @click="$emit('copy', 'grammar')"
    >
      <eg-icon icon="copy" size="small" />
      <span class=""> {{ $t("essays.copyGrammarReport") }} </span>
    </eg-b-dropdown-item>

    <eg-b-dropdown-divider />

    <eg-b-dropdown-item
      v-if="$listeners.delete"
      variant="danger"
      @click="onDelete"
    >
      <eg-icon icon="delete" size="small" />
      <span class=""> {{ $t("common.delete") }} </span>
    </eg-b-dropdown-item>
  </eg-b-dropdown>
</template>

<script>
import formatDate from "@/utils/formatDate";
import deleteConfirmationModalMixin from "@/mixins/deleteConfirmationModal";
import { PLATFORMS } from "@/constants";
import { mapGetters } from "vuex";

export default {
  name: "EssayActionsDropdown",

  // this.showDeleteConfirmationModal
  mixins: [deleteConfirmationModalMixin],
  inject: ["workspacesProvider"],

  props: {
    essay: {
      type: Object,
      required: true,
    },
    variant: {
      type: String,
      required: false,
      default: "outline-secondary",
    },
    customButtonClass: {
      type: String,
      default: "",
    },
  },

  computed: {
    ...mapGetters("config", ["features"]),

    showExportSection() {
      return (
        this.$listeners.print ||
        (this.$listeners.export && !this.isFailedStatus)
      );
    },
    isV3() {
      return (this.essay?.tags || []).includes("v3");
    },
    isFailedStatus() {
      return this.essay.status === "Failed";
    },
    folder() {
      return this.workspacesProvider.getFolderById(
        this.$route.params.workspaceKey,
        this.$route.params.id
      );
    },
    platform() {
      return this.folder?.platform;
    },
    isGoogleFolder() {
      return this.platform === PLATFORMS.GOOGLE;
    },
    isCanvasFolder() {
      return this.platform === PLATFORMS.CANVAS;
    },
    isFolderConnected() {
      return this.folder?.isConnected || false;
    },
  },

  methods: {
    formatDate,
    async printEssay() {
      this.$refs.dropdown.hide();
      await this.$nextTick();
      this.$emit("print", this.essay);
    },
    async onDelete() {
      try {
        const value = await this.showDeleteConfirmationModal({
          title: this.$t("essays.cannotAccessAfterDeletion"),
          message: this.$t("essays.cannotAccessAfterDeletion"),
        });
        value && this.$emit("delete", [this.essay.id]);
      } catch (error) {
        this.$showToastError(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.essay-actions-dropdown {
  &::v-deep {
    .btn {
      border: none;
    }

    .dropdown-toggle-no-caret {
      padding: 5px 3px;
      @media (min-width: 992px) {
        padding: 0.375rem 0.75rem;
      }
    }
  }

  &.custom-button-style {
    &::v-deep {
      .btn {
        border: none;
        padding: 0;
        background: none;

        &:hover,
        &:active,
        &:focus {
          background: none;
          border: none;
          box-shadow: none;
          text-decoration: none;
        }
      }

      &.show {
        .btn,
        .btn * {
          text-decoration: none;
        }
        .btn .eg-new-button {
          border-color: var(
            --color-components-button-primaryOutlined-buttonStroke
          );
          background-color: var(--color-surface-neutral-press);
        }
      }
      .btn:hover,
      .btn:hover * {
        text-decoration: none;
      }
    }
  }
}

.small-text {
  font-size: 12px;
  color: var(--gray);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: revert;
}

.dropdown-divider {
  margin: 0;
}
</style>
